// Polygon, rotated-box and corner-quad geometry, ported from frontend/tools/polygon_tool.py,
// frontend/tools/obb_tool.py and backend/app/models/models.quad_corners.
// tests/unit/shapes-golden.test.ts checks every function here against fixtures generated from the Python code.
import { pointInPolygon, rectContains, sceneLength, type Pt, type Rect } from "./geometry";

// ── Constants (screen pixels unless noted; converted to scene units where used) ────────────────
export const VERTEX_RADIUS = 5;
export const CLOSE_SNAP_DISTANCE = 12;
export const POLY_HANDLE_SIZE = 9;
export const ROTATE_HANDLE_RADIUS = 6;
export const ROTATE_HANDLE_GAP = 22;
export const ROTATE_SNAP_DEGREES = 15;
export const EDGE_SNAP_DISTANCE = 10;
export const MIN_VERTICES = 3;
/** Scene units: a rotated box drag smaller than this is a stray click (obb_tool.MIN_SIDE). */
export const OBB_MIN_SIDE = 3;

const rad = (deg: number) => (deg * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;
const sq = (cx: number, cy: number, half: number): Rect => ({ x1: cx - half, y1: cy - half, x2: cx + half, y2: cy + half });

/** Python's round(): halves go to the even neighbour (used by the Shift-snap while rotating). */
export function pyRound(x: number): number {
  const f = Math.floor(x), diff = x - f;
  if (diff < 0.5) return f;
  if (diff > 0.5) return f + 1;
  return f % 2 === 0 ? f : f + 1;
}

// ── Helpers shared by polygons ─────────────────────────────────────────────────────────────────
/** polygon_tool.rotate_points: turned by `degrees` (clockwise on screen) about `c`. */
export function rotatePoints(pts: Pt[], degrees: number, c: Pt): Pt[] {
  const r = rad(degrees), cos = Math.cos(r), sin = Math.sin(r);
  return pts.map(([x, y]) => {
    const dx = x - c[0], dy = y - c[1];
    return [c[0] + dx * cos - dy * sin, c[1] + dx * sin + dy * cos] as Pt;
  });
}

/** polygon_tool.centroid: the mean of the corners, the pivot for rotating. */
export function centroid(pts: Pt[]): Pt {
  const n = Math.max(pts.length, 1);
  return [pts.reduce((s, p) => s + p[0], 0) / n, pts.reduce((s, p) => s + p[1], 0) / n];
}

/** The point on segment a-b nearest to p, clamped to the segment (polygon_tool.closest_point_on_segment). */
export function closestPointOnSegment(a: Pt, b: Pt, p: Pt): Pt {
  const dx = b[0] - a[0], dy = b[1] - a[1], lenSq = dx * dx + dy * dy;
  if (lenSq <= 0) return [a[0], a[1]];
  const t = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq));
  return [a[0] + t * dx, a[1] + t * dy];
}

function boundsOf(pts: Pt[]): Rect {
  const xs = pts.map((q) => q[0]), ys = pts.map((q) => q[1]);
  return { x1: Math.min(...xs), y1: Math.min(...ys), x2: Math.max(...xs), y2: Math.max(...ys) };
}

/** A circle inscribed in a square rect: what QPainterPath.addEllipse covers. */
const inEllipse = (r: Rect, p: Pt) => {
  const cx = (r.x1 + r.x2) / 2, cy = (r.y1 + r.y2) / 2, rx = (r.x2 - r.x1) / 2, ry = (r.y2 - r.y1) / 2;
  return ((p[0] - cx) / rx) ** 2 + ((p[1] - cy) / ry) ** 2 <= 1;
};

// ── Polygon items ──────────────────────────────────────────────────────────────────────────────
/** PolygonItem._handle_rects: a square on every corner. */
export function polyHandleRects(pts: Pt[], scale: number): Rect[] {
  const hs = sceneLength(POLY_HANDLE_SIZE, scale) / 2;
  return pts.map(([x, y]) => sq(x, y, hs));
}

/** PolygonItem._handle_at: the first corner handle containing p; only a selected polygon has them. */
export function polyHandleAt(pts: Pt[], p: Pt, scale: number, selected: boolean): number | null {
  if (!selected) return null;
  const rects = polyHandleRects(pts, scale);
  for (let i = 0; i < rects.length; i++) if (rectContains(rects[i], p)) return i;
  return null;
}

/** PolygonItem._edge_at: the index a new corner would take and where on the edge, for a click near an edge. */
export function edgeAt(pts: Pt[], p: Pt, scale: number): { index: number; point: Pt } | null {
  const n = pts.length;
  if (n < 2) return null;
  const tol = sceneLength(EDGE_SNAP_DISTANCE, scale);
  let best: { d: number; index: number; point: Pt } | null = null;
  for (let i = 0; i < n; i++) {
    const point = closestPointOnSegment(pts[i], pts[(i + 1) % n], p);
    const d = Math.hypot(p[0] - point[0], p[1] - point[1]);
    if (d <= tol && (best === null || d < best.d)) best = { d, index: i, point };
  }
  return best && { index: best.index + 1, point: best.point };
}

export const insertVertex = (pts: Pt[], index: number, p: Pt): Pt[] => [...pts.slice(0, index), [p[0], p[1]] as Pt, ...pts.slice(index)];

/** PolygonItem.remove_vertex: null (and nothing changes) when only the last three are left. */
export function removeVertex(pts: Pt[], index: number): Pt[] | null {
  if (pts.length <= MIN_VERTICES) return null;
  return pts.filter((_, i) => i !== index);
}

export function polyGripCenter(pts: Pt[], scale: number): Pt {
  const b = boundsOf(pts);
  return [(b.x1 + b.x2) / 2, b.y1 - sceneLength(ROTATE_HANDLE_GAP, scale)];
}
export function polyGripRect(pts: Pt[], scale: number): Rect {
  const c = polyGripCenter(pts, scale);
  return sq(c[0], c[1], sceneLength(ROTATE_HANDLE_RADIUS, scale));
}
export const onPolyGrip = (pts: Pt[], p: Pt, scale: number, selected: boolean) => selected && rectContains(polyGripRect(pts, scale), p);

/**
 * PolygonItem.shape().contains: the filled outline, plus the corner handles and the grip when selected.
 * Qt fills that path odd-even, so a handle that overlaps the outline cancels out and its inner half misses;
 * a click on the inner half of a corner handle is a hit here (the one intended difference).
 */
export function polygonShapeContains(pts: Pt[], p: Pt, scale: number, selected: boolean): boolean {
  if (pointInPolygon(pts, p)) return true;
  if (!selected) return false;
  return polyHandleRects(pts, scale).some((r) => rectContains(r, p)) || inEllipse(polyGripRect(pts, scale), p);
}

/** A corner dragged to the pointer, kept on the image (PolygonItem._clamped). */
export function clampToImage(p: Pt, w: number, h: number): Pt {
  if (!(w > 0 && h > 0)) return p;
  return [Math.min(Math.max(p[0], 0), w), Math.min(Math.max(p[1], 0), h)];
}

export function dragVertex(orig: Pt[], index: number, p: Pt, w: number, h: number): Pt[] {
  const out = orig.map((q) => [q[0], q[1]] as Pt);
  out[index] = clampToImage(p, w, h);
  return out;
}

export const angleAbout = (c: Pt, p: Pt) => deg(Math.atan2(p[1] - c[1], p[0] - c[0]));

/** Dragging the grip: the angle swept since the press, optionally snapped to 15° (PolygonItem.mouseMoveEvent). */
export function rotateDrag(orig: Pt[], pivot: Pt, startAngle: number, p: Pt, shift: boolean): Pt[] {
  let delta = angleAbout(pivot, p) - startAngle;
  if (shift) delta = pyRound(delta / ROTATE_SNAP_DEGREES) * ROTATE_SNAP_DEGREES;
  return rotatePoints(orig, delta, pivot);
}

/** PolygonItem.rotate_by: turned about its centroid. */
export const rotatePolygonBy = (pts: Pt[], degrees: number): Pt[] => rotatePoints(pts, degrees, centroid(pts));

// ── Rotated boxes ──────────────────────────────────────────────────────────────────────────────
export interface Obb { cx: number; cy: number; w: number; h: number; angle: number }
export type ObbHandle = "tl" | "tr" | "br" | "bl";
export const OBB_HANDLES: ObbHandle[] = ["tl", "tr", "br", "bl"];
export const obbEqual = (a: Obb, b: Obb) => a.cx === b.cx && a.cy === b.cy && a.w === b.w && a.h === b.h && a.angle === b.angle;

/** obb_tool.corners_of: clockwise from top-left; angle in degrees, clockwise on screen. */
export function cornersOf(o: Obb): Pt[] {
  const r = rad(o.angle), cos = Math.cos(r), sin = Math.sin(r), hw = o.w / 2, hh = o.h / 2;
  return ([[-hw, -hh], [hw, -hh], [hw, hh], [-hw, hh]] as Pt[]).map(([dx, dy]) => [o.cx + dx * cos - dy * sin, o.cy + dx * sin + dy * cos] as Pt);
}

/** obb_tool.obb_from_corners: opposite sides are averaged, so a slightly off trace stays centred. */
export function obbFromCorners(pts: Pt[]): Obb {
  if (pts.length !== 4) throw new Error(`an OBB has four corners, got ${pts.length}`);
  const cx = pts.reduce((s, p) => s + p[0], 0) / 4, cy = pts.reduce((s, p) => s + p[1], 0) / 4;
  const dist = (a: Pt, b: Pt) => Math.hypot(b[0] - a[0], b[1] - a[1]);
  const w = (dist(pts[0], pts[1]) + dist(pts[3], pts[2])) / 2, h = (dist(pts[1], pts[2]) + dist(pts[0], pts[3])) / 2;
  return { cx, cy, w, h, angle: angleAbout(pts[0], pts[1]) };
}

/** obb_tool.is_rectangular: tight (0.5%) so a hand-traced quad is never squared up on load. */
export function isRectangular(pts: Pt[], tolerance = 0.005): boolean {
  if (pts.length !== 4) return false;
  const dist = (a: Pt, b: Pt) => Math.hypot(b[0] - a[0], b[1] - a[1]);
  const sides = [0, 1, 2, 3].map((i) => dist(pts[i], pts[(i + 1) % 4]));
  if (Math.min(...sides) <= 0) return false;
  for (const [a, b] of [[0, 2], [1, 3]]) if (Math.abs(sides[a] - sides[b]) / Math.max(sides[a], sides[b]) > tolerance) return false;
  const d1 = dist(pts[0], pts[2]), d2 = dist(pts[1], pts[3]);
  if (Math.max(d1, d2) <= 0) return false;
  return Math.abs(d1 - d2) / Math.max(d1, d2) <= tolerance;
}

export function obbHandleRects(o: Obb, scale: number): Record<ObbHandle, Rect> {
  const hs = sceneLength(POLY_HANDLE_SIZE, scale) / 2, c = cornersOf(o);
  return { tl: sq(c[0][0], c[0][1], hs), tr: sq(c[1][0], c[1][1], hs), br: sq(c[2][0], c[2][1], hs), bl: sq(c[3][0], c[3][1], hs) };
}

/** The grip stands off the middle of the top edge, along its outward normal. */
export function obbRotateCenter(o: Obb, scale: number): Pt {
  const gap = sceneLength(ROTATE_HANDLE_GAP, scale), r = rad(o.angle);
  return [o.cx + Math.sin(r) * (o.h / 2 + gap), o.cy - Math.cos(r) * (o.h / 2 + gap)];
}
export function obbRotateRect(o: Obb, scale: number): Rect {
  const c = obbRotateCenter(o, scale);
  return sq(c[0], c[1], sceneLength(ROTATE_HANDLE_RADIUS, scale));
}

export function obbHandleAt(o: Obb, p: Pt, scale: number, selected: boolean): ObbHandle | "rotate" | null {
  if (!selected) return null;
  if (rectContains(obbRotateRect(o, scale), p)) return "rotate";
  const rects = obbHandleRects(o, scale);
  return OBB_HANDLES.find((h) => rectContains(rects[h], p)) ?? null;
}

/** OBBItem.shape().contains; same odd-even note as polygonShapeContains. */
export function obbShapeContains(o: Obb, p: Pt, scale: number, selected: boolean): boolean {
  if (pointInPolygon(cornersOf(o), p)) return true;
  if (!selected) return false;
  const rects = obbHandleRects(o, scale);
  return OBB_HANDLES.some((h) => rectContains(rects[h], p)) || inEllipse(obbRotateRect(o, scale), p);
}

/** OBBItem._apply_resize: a corner dragged in the box's own rotated frame, the opposite corner fixed. */
export function obbResize(o: Obb, handle: ObbHandle, p: Pt): Obb {
  const r = rad(o.angle), cos = Math.cos(r), sin = Math.sin(r);
  const toLocal = (q: Pt): Pt => { const dx = q[0] - o.cx, dy = q[1] - o.cy; return [dx * cos + dy * sin, -dx * sin + dy * cos]; };
  const toScene = (lx: number, ly: number): Pt => [o.cx + lx * cos - ly * sin, o.cy + lx * sin + ly * cos];
  const hw = o.w / 2, hh = o.h / 2;
  const fixed = ({ tl: [hw, hh], tr: [-hw, hh], br: [-hw, -hh], bl: [hw, -hh] } as Record<ObbHandle, Pt>)[handle];
  const moving = toLocal(p);
  const w = Math.max(Math.abs(moving[0] - fixed[0]), OBB_MIN_SIDE), h = Math.max(Math.abs(moving[1] - fixed[1]), OBB_MIN_SIDE);
  const anchor = toScene(fixed[0], fixed[1]);
  const sx = fixed[0] > 0 ? -1 : 1, sy = fixed[1] > 0 ? -1 : 1;
  return {
    cx: anchor[0] + ((sx * w) / 2) * cos - ((sy * h) / 2) * sin,
    cy: anchor[1] + ((sx * w) / 2) * sin + ((sy * h) / 2) * cos,
    w, h, angle: o.angle,
  };
}

/** OBBItem._apply_rotation: the grip is 90° round from the top edge's own direction. */
export function obbRotateTo(o: Obb, p: Pt, shift: boolean): Obb {
  let angle = angleAbout([o.cx, o.cy], p) + 90;
  if (shift) angle = pyRound(angle / ROTATE_SNAP_DEGREES) * ROTATE_SNAP_DEGREES;
  return { ...o, angle };
}

export const obbRotateBy = (o: Obb, degrees: number): Obb => ({ ...o, angle: o.angle + degrees });

// ── Corner quads ───────────────────────────────────────────────────────────────────────────────
/**
 * models.quad_corners: four corners in the canonical order the corner model trains on — sorted by angle
 * about the centroid (clockwise on screen), then rotated to start at the corner nearest the image's
 * top-left. null for anything that is not a quadrilateral with area.
 */
export function quadCorners(pts: Pt[] | null | undefined): Pt[] | null {
  if (!pts || pts.length !== 4) return null;
  const cx = pts.reduce((s, p) => s + p[0], 0) / 4, cy = pts.reduce((s, p) => s + p[1], 0) / 4;
  const ordered = pts.map((p) => [p[0], p[1]] as Pt).sort((a, b) => Math.atan2(a[1] - cy, a[0] - cx) - Math.atan2(b[1] - cy, b[0] - cx));
  let area = 0;
  for (let i = 0; i < 4; i++) {
    const [x0, y0] = ordered[i], [x1, y1] = ordered[(i + 1) % 4];
    area += x0 * y1 - x1 * y0;
  }
  if (Math.abs(area) / 2 <= 0) return null;
  let start = 0;
  const key = (i: number): Pt3 => [ordered[i][0] + ordered[i][1], ordered[i][1], ordered[i][0]];
  for (let i = 1; i < 4; i++) if (lessTuple(key(i), key(start))) start = i;
  return [...ordered.slice(start), ...ordered.slice(0, start)];
}
type Pt3 = [number, number, number];
const lessTuple = (a: Pt3, b: Pt3) => { for (let i = 0; i < 3; i++) { if (a[i] !== b[i]) return a[i] < b[i]; } return false; };
